import React, { useEffect, useMemo, useState } from "react";
import { Settings, X } from "lucide-react";
import DatePicker from "../../Common/DatePicker";
import PolicyFormWizardShell from "../policy-wizard/PolicyFormWizardShell";
import PolicyApplicabilityStepSection from "../policy-wizard/PolicyApplicabilityStepSection";
import EmployeeMultiSelectField, {
  normalizeEmployeeOption,
  extractEmployeesFromResponse,
} from "../policy-wizard/EmployeeMultiSelectField";
import { pwInput, pwLabel, pwSectionTitle } from "../policy-wizard/policyWizardFieldStyles";
import LeaveGroupWorkflowSection from "./components/LeaveGroupWorkflowSection";
import LeaveTypeApprovalWorkflowSection from "./components/LeaveTypeApprovalWorkflowSection";
import { normalizeLeaveGroupWorkflow } from "../../../lib/leaveApprovalWorkflow";
import { resolveCompanyMinDateValue } from "../../../utils/selectableDateBounds";
import { employeeService } from "../../../services/api";
import {
  findLeaveGroupApplicabilityConflicts,
} from "../../../utils/leaveGroupApplicabilityConflicts";

export const LEAVE_GROUP_WIZARD_STEPS = [
  { id: 1, label: "Basic details", shortLabel: "Basics" },
  { id: 2, label: "Leave types", shortLabel: "Types" },
  { id: 3, label: "Leave workflow", shortLabel: "Workflow" },
  { id: 4, label: "Applicability", shortLabel: "Applicability" },
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const LTC_STEPS = [
  { key: "credit", label: "Credit" },
  { key: "accumulation", label: "Accumulation" },
  { key: "usageRules", label: "Availment" },
  { key: "approvalWorkflow", label: "Approval" },
];

const asId = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  if (Number.isFinite(n) && String(n) === String(value).trim()) return n;
  return value;
};

const toIdArray = (value) => {
  const id = asId(value);
  return id == null ? [] : [id];
};

const firstId = (arr) =>
  Array.isArray(arr) && arr.length > 0 ? String(arr[0]) : "";

/** assignedTo (API arrays) → PolicyApplicabilityStepSection shape */
export function assignedToToApplicability(assignedTo = {}) {
  const selectedFromDetails = Array.isArray(assignedTo.selectedEmployees)
    ? assignedTo.selectedEmployees.map(normalizeEmployeeOption).filter(Boolean)
    : [];
  const selectedFromIds = Array.isArray(assignedTo.employees)
    ? assignedTo.employees
        .map((id) => {
          const found = selectedFromDetails.find((e) => String(e.id) === String(id));
          return (
            found ||
            normalizeEmployeeOption({ id, name: `Employee ${id}`, empCode: "", email: "" })
          );
        })
        .filter(Boolean)
    : selectedFromDetails;

  return {
    allEmployees: !!assignedTo.allEmployees,
    branch: firstId(assignedTo.branches),
    department: firstId(assignedTo.departments),
    designation: firstId(assignedTo.designations),
    grade: firstId(assignedTo.grades),
    category: firstId(assignedTo.categories),
    subDepartment: firstId(assignedTo.subDepartments),
    location: "",
    employmentTypes: Array.isArray(assignedTo.employmentTypes)
      ? assignedTo.employmentTypes
      : [],
    individualEmployees: selectedFromIds,
  };
}

/** Applicability policy shape → leave group assignedTo */
export function applicabilityToAssignedTo(policy = {}) {
  const selected = Array.isArray(policy.individualEmployees)
    ? policy.individualEmployees.map(normalizeEmployeeOption).filter(Boolean)
    : [];
  const employeeIds = selected
    .map((e) => Number(e.id))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (policy.allEmployees) {
    return {
      allEmployees: true,
      branches: [],
      departments: [],
      designations: [],
      grades: [],
      subDepartments: [],
      categories: [],
      // Employment types refine "all employees" (e.g. all Full-time). Do not clear on toggle.
      employmentTypes: Array.isArray(policy.employmentTypes)
        ? policy.employmentTypes
        : [],
      employees: [],
      selectedEmployees: [],
    };
  }

  return {
    allEmployees: false,
    branches: toIdArray(policy.branch),
    departments: toIdArray(policy.department),
    designations: toIdArray(policy.designation),
    grades: toIdArray(policy.grade),
    categories: toIdArray(policy.category),
    subDepartments: toIdArray(policy.subDepartment),
    employmentTypes: Array.isArray(policy.employmentTypes)
      ? policy.employmentTypes
      : [],
    employees: employeeIds,
    selectedEmployees: selected,
  };
}

function ConfigureLeaveTypeModal({
  darkMode,
  leaveType,
  cfg,
  updateLeaveTypeConfig,
  onClose,
}) {
  const [stepKey, setStepKey] = useState("credit");
  const stepIndex = Math.max(
    0,
    LTC_STEPS.findIndex((s) => s.key === stepKey)
  );
  const inputCls = pwInput(darkMode);
  const labelCls = pwLabel(darkMode);
  const radioGrpCls = "flex items-center gap-4";
  const radioLabelCls = `flex items-center gap-2 cursor-pointer text-sm ${
    darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"
  }`;

  const companyMinDate = useMemo(() => resolveCompanyMinDateValue() || undefined, []);

  const expiryEnabled =
    (cfg.accumulation?.expiryRuleEnabled ??
      (cfg.accumulation?.expiryRule ? "Yes" : "No")) === "Yes";
  const expiryHowRaw =
    cfg.accumulation?.expiryHow ??
    cfg.accumulation?.expiryRule ??
    "Lapse Immediately";
  const expiryHow =
    expiryHowRaw === "Lapse after X months" || expiryHowRaw === "Never Lapse"
      ? "Lapse Immediately"
      : expiryHowRaw;

  const goNext = () => {
    if (stepIndex < LTC_STEPS.length - 1) setStepKey(LTC_STEPS[stepIndex + 1].key);
  };
  const goPrev = () => {
    if (stepIndex > 0) setStepKey(LTC_STEPS[stepIndex - 1].key);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className={`flex max-h-[88vh] w-[92vw] max-w-5xl flex-col overflow-hidden rounded-xl shadow-xl ${
          darkMode ? "bg-[hsl(var(--ds-surface))] text-white" : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex shrink-0 items-center justify-between border-b px-5 py-4 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <h3 className="text-lg font-semibold">Configure {leaveType.name}</h3>
          <button
            type="button"
            onClick={onClose}
            className={`rounded p-1 ${darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"}`}
          >
            <X size={20} />
          </button>
        </div>

        <PolicyFormWizardShell
          darkMode={darkMode}
          wizardLabel="Leave type settings"
          steps={LTC_STEPS.map((s, i) => ({
            id: i + 1,
            label: s.label,
            shortLabel: s.label,
          }))}
          currentStep={stepIndex + 1}
          onStepClick={(id) => {
            if (id <= stepIndex + 1) setStepKey(LTC_STEPS[id - 1].key);
          }}
          footer={
            <>
              <button
                type="button"
                onClick={onClose}
                className={`rounded px-4 py-2 text-sm font-medium ${
                  darkMode
                    ? "bg-[hsl(var(--ds-muted))] text-white hover:bg-[hsl(var(--ds-muted))]"
                    : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={goPrev}
                disabled={stepIndex === 0}
                className={`rounded px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
                  darkMode
                    ? "bg-[hsl(var(--ds-muted))] text-white hover:bg-[hsl(var(--ds-muted))]"
                    : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                }`}
              >
                Previous
              </button>
              {stepIndex < LTC_STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  className="rounded px-4 py-2 text-sm font-medium inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                >
                  Next: {LTC_STEPS[stepIndex + 1].label}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded px-4 py-2 text-sm font-medium inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                >
                  Done
                </button>
              )}
            </>
          }
        >
          <div className="mb-4 max-w-xs">
            <label className={labelCls}>Effective From Date</label>
            <DatePicker
              value={cfg.basic?.effectiveFromDate ?? ""}
              onChange={(e) =>
                updateLeaveTypeConfig(leaveType.id, "basic", {
                  effectiveFromDate: e?.target?.value ?? e,
                })
              }
              min={companyMinDate}
              minErrorText="Effective from cannot be before service commencement"
            />
          </div>

          {stepKey === "credit" && (
            <div>
              <h4 className={pwSectionTitle(darkMode)}>Credit</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelCls}>Credit Frequency</label>
                  <select
                    value={cfg.credit?.creditFrequency ?? "Yearly"}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "credit", {
                        creditFrequency: e.target.value,
                      })
                    }
                    className={inputCls}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Half-Yearly">Half-Yearly</option>
                    <option value="Yearly">Yearly</option>
                    <option value="One-time">One-time</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Credit Method</label>
                  <select
                    value={cfg.credit?.creditMethod ?? "Fixed Days"}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "credit", {
                        creditMethod: e.target.value,
                      })
                    }
                    className={inputCls}
                  >
                    <option value="Fixed Days">Fixed Days</option>
                    <option value="Based on Working Days">Based on Working Days</option>
                    <option value="Based on Attendance">Based on Attendance</option>
                  </select>
                </div>
                {cfg.credit?.creditMethod === "Fixed Days" && (
                  <div>
                    <label className={labelCls}>No. of days to credit</label>
                    <input
                      type="number"
                      min={0}
                      value={cfg.credit?.daysToCredit ?? ""}
                      onChange={(e) =>
                        updateLeaveTypeConfig(leaveType.id, "credit", {
                          daysToCredit: e.target.value,
                        })
                      }
                      className={inputCls}
                      placeholder="Days"
                    />
                  </div>
                )}
                {(cfg.credit?.creditMethod === "Based on Attendance" ||
                  cfg.credit?.creditMethod === "Based on Working Days") && (
                  <>
                    <div>
                      <label className={labelCls}>
                        {cfg.credit?.creditMethod === "Based on Attendance"
                          ? "On how many days attendance"
                          : "On how many working days"}
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={cfg.credit?.attendanceDaysRequired ?? ""}
                        onChange={(e) =>
                          updateLeaveTypeConfig(leaveType.id, "credit", {
                            attendanceDaysRequired: e.target.value,
                          })
                        }
                        className={inputCls}
                        placeholder="e.g. 22"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>How many leaves to credit</label>
                      <input
                        type="number"
                        min={0}
                        value={cfg.credit?.leavesCreditedPerAttendanceDays ?? ""}
                        onChange={(e) =>
                          updateLeaveTypeConfig(leaveType.id, "credit", {
                            leavesCreditedPerAttendanceDays: e.target.value,
                          })
                        }
                        className={inputCls}
                        placeholder="e.g. 1"
                      />
                    </div>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] md:col-span-2 lg:col-span-3">
                      {cfg.credit?.creditMethod === "Based on Attendance"
                        ? "Counts Process Attendance paid days for the period (PR, WO, PH, OD, CO, and paid leave types). Unpaid / LOP and sandwich unpaid WO are excluded."
                        : "Counts only days worked from or for office (PR, OD, CO). Paid leaves, LOP, weekly off, and public holidays are not working days."}
                    </p>
                  </>
                )}
                <div>
                  <label className={labelCls}>Pro-rata for mid-joining</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`proRata-${leaveType.id}`}
                        checked={(cfg.credit?.proRataMidJoining ?? "No") === "Yes"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "credit", {
                            proRataMidJoining: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`proRata-${leaveType.id}`}
                        checked={(cfg.credit?.proRataMidJoining ?? "No") === "No"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "credit", {
                            proRataMidJoining: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Credit on</label>
                  <select
                    value={cfg.credit?.creditOn ?? "Start of Period"}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "credit", {
                        creditOn: e.target.value,
                      })
                    }
                    className={inputCls}
                  >
                    <option value="Start of Period">Start of Period</option>
                    <option value="End of Period">End of Period</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {stepKey === "accumulation" && (
            <div>
              <h4 className={pwSectionTitle(darkMode)}>Accumulation & Carry Forward</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelCls}>Maximum Accumulation Limit</label>
                  <input
                    type="number"
                    value={cfg.accumulation?.maximumAccumulationLimit ?? ""}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "accumulation", {
                        maximumAccumulationLimit: e.target.value,
                      })
                    }
                    className={inputCls}
                    placeholder="Days"
                  />
                </div>
                <div>
                  <label className={labelCls}>Carry Forward Limit</label>
                  <input
                    type="number"
                    value={cfg.accumulation?.carryForwardLimit ?? ""}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "accumulation", {
                        carryForwardLimit: e.target.value,
                      })
                    }
                    className={inputCls}
                    placeholder="Days"
                  />
                </div>
                <div>
                  <label className={labelCls}>Expiry Rule</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`expiryRule-${leaveType.id}`}
                        checked={expiryEnabled}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "accumulation", {
                            expiryRuleEnabled: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`expiryRule-${leaveType.id}`}
                        checked={!expiryEnabled}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "accumulation", {
                            expiryRuleEnabled: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                {expiryEnabled && (
                  <>
                    <div>
                      <label className={labelCls}>How</label>
                      <select
                        value={expiryHow}
                        onChange={(e) =>
                          updateLeaveTypeConfig(leaveType.id, "accumulation", {
                            expiryHow: e.target.value,
                          })
                        }
                        className={inputCls}
                      >
                        <option value="Lapse Immediately">Lapse Immediately</option>
                        <option value="Lapse in specific month">
                          Lapse in specific month
                        </option>
                      </select>
                    </div>
                    {expiryHow === "Lapse in specific month" && (
                      <div>
                        <label className={labelCls}>Which month to lapse</label>
                        <select
                          value={cfg.accumulation?.lapseMonth ?? ""}
                          onChange={(e) =>
                            updateLeaveTypeConfig(leaveType.id, "accumulation", {
                              lapseMonth: e.target.value,
                            })
                          }
                          className={inputCls}
                        >
                          <option value="">Select month</option>
                          {MONTHS.map((m, i) => (
                            <option key={m} value={String(i + 1)}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}
                <div>
                  <label className={labelCls}>Encashment Allowed</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`encash-${leaveType.id}`}
                        checked={(cfg.accumulation?.encashmentAllowed ?? "No") === "Yes"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "accumulation", {
                            encashmentAllowed: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`encash-${leaveType.id}`}
                        checked={(cfg.accumulation?.encashmentAllowed ?? "No") === "No"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "accumulation", {
                            encashmentAllowed: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                {(cfg.accumulation?.encashmentAllowed === "Yes") && (
                  <>
                    <div>
                      <label className={labelCls}>Encashment Limit</label>
                      <input
                        type="number"
                        value={cfg.accumulation?.encashmentLimit ?? ""}
                        onChange={(e) =>
                          updateLeaveTypeConfig(leaveType.id, "accumulation", {
                            encashmentLimit: e.target.value,
                          })
                        }
                        className={inputCls}
                        placeholder="Days"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Encashment Calculation Method</label>
                      <select
                        value={cfg.accumulation?.encashmentCalculationMethod ?? "Basic"}
                        onChange={(e) =>
                          updateLeaveTypeConfig(leaveType.id, "accumulation", {
                            encashmentCalculationMethod: e.target.value,
                          })
                        }
                        className={inputCls}
                      >
                        <option value="Basic">Basic</option>
                        <option value="Gross">Gross</option>
                        <option value="Custom formula">Custom formula</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {stepKey === "usageRules" && (
            <div>
              <h4 className={pwSectionTitle(darkMode)}>Availment</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelCls}>Minimum Leave Units (day)</label>
                  <select
                    value={cfg.usageRules?.minimumLeaveUnits ?? "1"}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "usageRules", {
                        minimumLeaveUnits: e.target.value,
                      })
                    }
                    className={inputCls}
                  >
                    <option value="0.5">0.5</option>
                    <option value="1">1</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Maximum Leave per Request</label>
                  <input
                    type="number"
                    value={cfg.usageRules?.maximumLeavePerRequest ?? ""}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "usageRules", {
                        maximumLeavePerRequest: e.target.value,
                      })
                    }
                    className={inputCls}
                    placeholder="Days"
                  />
                </div>
                <div>
                  <label className={labelCls}>Consecutive Leave Limit</label>
                  <input
                    type="number"
                    value={cfg.usageRules?.consecutiveLeaveLimit ?? ""}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "usageRules", {
                        consecutiveLeaveLimit: e.target.value,
                      })
                    }
                    className={inputCls}
                    placeholder="Days"
                  />
                </div>
                <div>
                  <label className={labelCls}>Sandwich Rule</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`sandwich-${leaveType.id}`}
                        checked={(cfg.usageRules?.sandwichRule ?? "Disable") === "Enable"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            sandwichRule: "Enable",
                          })
                        }
                      />{" "}
                      Enable
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`sandwich-${leaveType.id}`}
                        checked={(cfg.usageRules?.sandwichRule ?? "Disable") === "Disable"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            sandwichRule: "Disable",
                          })
                        }
                      />{" "}
                      Disable
                    </label>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Clubbing Restrictions</label>
                  <input
                    type="text"
                    value={(cfg.usageRules?.clubbingRestrictions || []).join(", ")}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "usageRules", {
                        clubbingRestrictions: e.target.value
                          ? e.target.value
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean)
                          : [],
                      })
                    }
                    className={inputCls}
                    placeholder="Comma-separated"
                  />
                </div>
                <div>
                  <label className={labelCls}>Advance Leave Allowed</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`advance-${leaveType.id}`}
                        checked={(cfg.usageRules?.advanceLeaveAllowed ?? "No") === "Yes"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            advanceLeaveAllowed: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`advance-${leaveType.id}`}
                        checked={(cfg.usageRules?.advanceLeaveAllowed ?? "No") === "No"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            advanceLeaveAllowed: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                {(cfg.usageRules?.advanceLeaveAllowed === "Yes") && (
                  <div>
                    <label className={labelCls}>Limit of advance leave (days)</label>
                    <input
                      type="number"
                      min={0}
                      value={cfg.usageRules?.advanceLeaveLimit ?? ""}
                      onChange={(e) =>
                        updateLeaveTypeConfig(leaveType.id, "usageRules", {
                          advanceLeaveLimit: e.target.value,
                        })
                      }
                      className={inputCls}
                      placeholder="Days"
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Negative Balance Allowed</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`negBal-${leaveType.id}`}
                        checked={(cfg.usageRules?.negativeBalanceAllowed ?? "No") === "Yes"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            negativeBalanceAllowed: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`negBal-${leaveType.id}`}
                        checked={(cfg.usageRules?.negativeBalanceAllowed ?? "No") === "No"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            negativeBalanceAllowed: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                {(cfg.usageRules?.negativeBalanceAllowed === "Yes") && (
                  <div>
                    <label className={labelCls}>Negative Balance Limit</label>
                    <input
                      type="number"
                      value={cfg.usageRules?.negativeBalanceLimit ?? ""}
                      onChange={(e) =>
                        updateLeaveTypeConfig(leaveType.id, "usageRules", {
                          negativeBalanceLimit: e.target.value,
                        })
                      }
                      className={inputCls}
                      placeholder="Days"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className={labelCls}>Avail balance only from next year</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`availNextYr-${leaveType.id}`}
                        checked={(cfg.usageRules?.availOnlyFromNextYear ?? "No") === "Yes"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            availOnlyFromNextYear: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`availNextYr-${leaveType.id}`}
                        checked={(cfg.usageRules?.availOnlyFromNextYear ?? "No") === "No"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            availOnlyFromNextYear: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Allow Leave in Notice Period</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`notice-${leaveType.id}`}
                        checked={(cfg.usageRules?.noticePeriodRestriction ?? "No") === "Yes"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            noticePeriodRestriction: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`notice-${leaveType.id}`}
                        checked={(cfg.usageRules?.noticePeriodRestriction ?? "No") === "No"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            noticePeriodRestriction: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                {(cfg.usageRules?.noticePeriodRestriction === "Yes") && (
                  <div>
                    <label className={labelCls}>Limit (days)</label>
                    <input
                      type="number"
                      min={0}
                      value={cfg.usageRules?.noticePeriodDays ?? ""}
                      onChange={(e) =>
                        updateLeaveTypeConfig(leaveType.id, "usageRules", {
                          noticePeriodDays: e.target.value,
                        })
                      }
                      className={inputCls}
                      placeholder="Days"
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Allow Leave in Probation</label>
                  <div className={radioGrpCls}>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`probation-${leaveType.id}`}
                        checked={(cfg.usageRules?.probationRestriction ?? "No") === "Yes"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            probationRestriction: "Yes",
                          })
                        }
                      />{" "}
                      Yes
                    </label>
                    <label className={radioLabelCls}>
                      <input
                        type="radio"
                        name={`probation-${leaveType.id}`}
                        checked={(cfg.usageRules?.probationRestriction ?? "No") === "No"}
                        onChange={() =>
                          updateLeaveTypeConfig(leaveType.id, "usageRules", {
                            probationRestriction: "No",
                          })
                        }
                      />{" "}
                      No
                    </label>
                  </div>
                </div>
                {(cfg.usageRules?.probationRestriction === "Yes") && (
                  <div>
                    <label className={labelCls}>Limit (days)</label>
                    <input
                      type="number"
                      min={0}
                      value={cfg.usageRules?.probationDays ?? ""}
                      onChange={(e) =>
                        updateLeaveTypeConfig(leaveType.id, "usageRules", {
                          probationDays: e.target.value,
                        })
                      }
                      className={inputCls}
                      placeholder="Days"
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Leave balance rounding</label>
                  <select
                    value={cfg.usageRules?.balanceRounding ?? "full_day"}
                    onChange={(e) =>
                      updateLeaveTypeConfig(leaveType.id, "usageRules", {
                        balanceRounding: e.target.value,
                      })
                    }
                    className={inputCls}
                  >
                    <option value="full_day">Nearest full day</option>
                    <option value="half_day">Half day</option>
                    <option value="quarter_day">Quarter day</option>
                    <option value="none">No rounding</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {stepKey === "approvalWorkflow" && (
            <LeaveTypeApprovalWorkflowSection
              darkMode={darkMode}
              cfg={cfg}
              leaveTypeId={leaveType.id}
              leaveTypeRequiresApproval={leaveType.requiresApproval !== false}
              updateLeaveTypeConfig={updateLeaveTypeConfig}
            />
          )}
        </PolicyFormWizardShell>
      </div>
    </div>
  );
}

/**
 * Leave Group create/edit multi-step wizard (Onboarding policy UX).
 */
export default function LeaveGroupFormWizard({
  darkMode = false,
  leaveGroupForm,
  setLeaveGroupForm,
  leaveTypes = [],
  getDefaultLeaveTypeConfig,
  updateLeaveTypeConfig,
  updateLeaveGroupWorkflow,
  currentStep = 1,
  onStepChange,
  stepError = null,
  branchesList = [],
  departmentsList = [],
  designationsList = [],
  gradesList = [],
  categoriesList = [],
  subDepartmentsList = [],
  employmentTypeOptions,
  existingLeaveGroups = [],
  editingLeaveGroupId = null,
  footer,
}) {
  const [configuringLeaveType, setConfiguringLeaveType] = useState(null);

  const applicabilityPolicy = useMemo(
    () => assignedToToApplicability(leaveGroupForm?.assignedTo),
    [leaveGroupForm?.assignedTo]
  );

  const applicabilityConflicts = useMemo(() => {
    const selected = applicabilityPolicy.individualEmployees || [];
    const labelById = {};
    selected.forEach((e) => {
      if (e?.id != null) {
        labelById[e.id] = e.empCode ? `${e.empCode} - ${e.name}` : e.name;
      }
    });
    return findLeaveGroupApplicabilityConflicts({
      candidateAssignedTo: leaveGroupForm?.assignedTo || {},
      existingGroups: existingLeaveGroups,
      excludeGroupId: editingLeaveGroupId,
      employeeLabelById: labelById,
    });
  }, [
    leaveGroupForm?.assignedTo,
    existingLeaveGroups,
    editingLeaveGroupId,
    applicabilityPolicy.individualEmployees,
  ]);

  // Hydrate chip labels when edit loads only numeric employee ids.
  useEffect(() => {
    const assigned = leaveGroupForm?.assignedTo || {};
    const ids = Array.isArray(assigned.employees)
      ? assigned.employees.map(Number).filter((id) => id > 0)
      : [];
    if (!ids.length) return;
    const details = Array.isArray(assigned.selectedEmployees) ? assigned.selectedEmployees : [];
    const needsHydrate = ids.some((id) => {
      const d = details.find((e) => Number(e?.id) === id);
      return !d?.empCode && (!d?.name || String(d.name).startsWith("Employee "));
    });
    if (!details.length || needsHydrate) {
      let cancelled = false;
      (async () => {
        try {
          const response = await employeeService.getEmployees({
            page: 1,
            limit: Math.max(200, ids.length),
          });
          const all = extractEmployeesFromResponse(response)
            .map(normalizeEmployeeOption)
            .filter(Boolean);
          const byId = new Map(all.map((e) => [Number(e.id), e]));
          const hydrated = ids
            .map((id) => byId.get(id) || normalizeEmployeeOption({ id, name: `Employee ${id}` }))
            .filter(Boolean);
          if (cancelled || !hydrated.length) return;
          setLeaveGroupForm((prev) => ({
            ...prev,
            assignedTo: {
              ...(prev.assignedTo || {}),
              employees: ids,
              selectedEmployees: hydrated,
            },
          }));
        } catch (error) {
          console.error("Failed to hydrate leave group employees:", error);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [leaveGroupForm?.assignedTo?.employees?.join?.(",")]);

  const setApplicabilityPolicy = (updater) => {
    setLeaveGroupForm((prev) => {
      const current = assignedToToApplicability(prev.assignedTo);
      const next = typeof updater === "function" ? updater(current) : updater;
      return {
        ...prev,
        assignedTo: applicabilityToAssignedTo(next),
      };
    });
  };

  const toggleEmploymentType = (value) => {
    setApplicabilityPolicy((p) => {
      const list = Array.isArray(p.employmentTypes) ? [...p.employmentTypes] : [];
      const idx = list.indexOf(value);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(value);
      return { ...p, employmentTypes: list };
    });
  };

  const configuringCfg = configuringLeaveType
    ? (leaveGroupForm.leaveTypeConfigurations || {})[configuringLeaveType.id] ??
      (leaveGroupForm.leaveTypeConfigurations || {})[
        String(configuringLeaveType.id)
      ] ??
      getDefaultLeaveTypeConfig(configuringLeaveType)
    : null;

  return (
    <>
      <PolicyFormWizardShell
        darkMode={darkMode}
        wizardLabel="Leave group wizard"
        steps={LEAVE_GROUP_WIZARD_STEPS}
        currentStep={currentStep}
        onStepClick={(id) => {
          if (id < currentStep) onStepChange?.(id);
        }}
        policySaveError={stepError}
        footer={footer}
      >
        {currentStep === 1 && (
          <div className="space-y-4">
            <h4 className={pwSectionTitle(darkMode)}>Basic details</h4>
            <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              Name this leave group and add an optional description for admins.
            </p>
            <div>
              <label className={pwLabel(darkMode)}>Leave Group Name *</label>
              <input
                type="text"
                value={leaveGroupForm.name || ""}
                onChange={(e) =>
                  setLeaveGroupForm((prev) => ({ ...prev, name: e.target.value }))
                }
                className={pwInput(darkMode)}
                placeholder="e.g., Standard Leave Group"
              />
            </div>
            <div>
              <label className={pwLabel(darkMode)}>Description</label>
              <textarea
                value={leaveGroupForm.description || ""}
                onChange={(e) =>
                  setLeaveGroupForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className={pwInput(darkMode)}
                placeholder="Enter leave group description"
                rows={4}
              />
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            <h4 className={pwSectionTitle(darkMode)}>Select Leave Types</h4>
            {/*<p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              Choose leave types for this group — you can leave none selected to create a
              no-leave entitlement group. Applicability can use All employees, org filters,
              and/or Individual employees. Use the gear to configure each selected type.
            </p>
             {!(leaveGroupForm.leaveTypes || []).length ? (
              <div
                className={`rounded-lg border px-3 py-2 text-sm ${
                  darkMode
                    ? "border-amber-700/50 bg-amber-950/30 text-amber-100"
                    : "border-amber-200 bg-amber-50 text-amber-900"
                }`}
              >
                No leave types selected — employees in this group will have no leave
                entitlement. You can still continue and set Applicability as usual.
              </div>
            ) : null} */}
            {leaveTypes.length === 0 ? (
              <p className={`text-sm ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                No leave types available yet. You can still save a no-leave group, or create leave
                types first.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {leaveTypes.map((leaveType) => {
                  const isSelected = (leaveGroupForm.leaveTypes || []).some(
                    (lt) => lt.id === leaveType.id
                  );
                  return (
                    <div
                      key={leaveType.id}
                      className={`flex items-start justify-between gap-2 rounded-lg border p-3 ${
                        isSelected
                          ? darkMode
                            ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-muted))]/50"
                            : "border-[hsl(var(--ds-primary))]/40 bg-red-50/40"
                          : darkMode
                            ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]/40"
                            : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setLeaveGroupForm((prev) => ({
                                ...prev,
                                leaveTypes: [...(prev.leaveTypes || []), leaveType],
                                leaveTypeConfigurations: {
                                  ...prev.leaveTypeConfigurations,
                                  [leaveType.id]:
                                    prev.leaveTypeConfigurations?.[leaveType.id] ??
                                    getDefaultLeaveTypeConfig(leaveType),
                                },
                              }));
                            } else {
                              setLeaveGroupForm((prev) => {
                                const next = {
                                  ...(prev.leaveTypeConfigurations || {}),
                                };
                                delete next[leaveType.id];
                                return {
                                  ...prev,
                                  leaveTypes: (prev.leaveTypes || []).filter(
                                    (lt) => lt.id !== leaveType.id
                                  ),
                                  leaveTypeConfigurations: next,
                                };
                              });
                              if (configuringLeaveType?.id === leaveType.id) {
                                setConfiguringLeaveType(null);
                              }
                            }
                          }}
                          className="mt-0.5 flex-shrink-0 rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))] focus:ring-[hsl(var(--ds-ring))]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div
                              className="h-3 w-3 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: leaveType.color }}
                            />
                            <span className="truncate text-sm font-medium">
                              {leaveType.name}
                            </span>
                          </div>
                          <span className="ml-5 text-xs text-[hsl(var(--ds-muted-fg))]">
                            ({leaveType.code})
                          </span>
                        </div>
                      </label>
                      <button
                        type="button"
                        disabled={!isSelected}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isSelected) return;
                          setConfiguringLeaveType(leaveType);
                        }}
                        className={`flex-shrink-0 rounded-lg p-1.5 transition-colors ${
                          isSelected
                            ? darkMode
                              ? "bg-[hsl(var(--ds-muted))] text-white hover:bg-[hsl(var(--ds-muted))]"
                              : "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                            : darkMode
                              ? "cursor-not-allowed bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))] opacity-50"
                              : "cursor-not-allowed bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] opacity-60"
                        }`}
                        title={
                          isSelected
                            ? "Configure leave type settings"
                            : "Select leave type first"
                        }
                      >
                        <Settings size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-3">
            <h4 className={pwSectionTitle(darkMode)}>Leave Workflow</h4>
            <p className={`text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
              Default approval path for leave requests in this group. Individual leave
              types can override via their gear settings.
            </p>
            <LeaveGroupWorkflowSection
              darkMode={darkMode}
              workflow={normalizeLeaveGroupWorkflow(
                leaveGroupForm.configurations?.leaveWorkflow
              )}
              onChange={updateLeaveGroupWorkflow}
            />
          </div>
        )}

        {currentStep === 4 && (
          <PolicyApplicabilityStepSection
            darkMode={darkMode}
            policy={applicabilityPolicy}
            setPolicy={setApplicabilityPolicy}
            branchesList={branchesList}
            departmentsList={departmentsList}
            designationsList={designationsList}
            gradesList={gradesList}
            categoriesList={categoriesList}
            subDepartmentsList={subDepartmentsList}
            employmentTypeOptions={employmentTypeOptions}
            onToggleEmploymentType={toggleEmploymentType}
            title="Applicability"
            allEmployeesLabel="Select All (Applicable to all employees)"
            showLocation={false}
          >
            <EmployeeMultiSelectField
              darkMode={darkMode}
              disabled={!!applicabilityPolicy.allEmployees}
              value={applicabilityPolicy.individualEmployees || []}
              onChange={(next) =>
                setApplicabilityPolicy((p) => ({
                  ...p,
                  individualEmployees: next,
                }))
              }
              label="Individual employees (optional — exclusive exception)"
              helpText="People listed here always use this leave group (they ignore other groups' All employees / org filters). They only conflict if the same person is already individually selected on another leave group."
            />
            {applicabilityConflicts.length > 0 ? (
              <div
                className={`mt-4 rounded-lg border px-4 py-3 ${
                  darkMode
                    ? "border-amber-700/60 bg-amber-950/40 text-amber-100"
                    : "border-amber-200 bg-amber-50 text-amber-950"
                }`}
              >
                <h5 className="mb-1 text-sm font-semibold text-amber-700">
                  Applicability conflicts
                </h5>
                <p className="mb-2 text-xs">
                  Org filters / All employees cannot overlap another leave group&apos;s org / All
                  scope. Individually selected employees do not conflict with other groups&apos;
                  org filters — only with the same person already picked individually elsewhere.
                </p>
                <ul className="list-disc space-y-1 pl-5 text-sm">
                  {applicabilityConflicts.map((c, idx) => (
                    <li key={`${c.type}-${c.otherGroup?.id || idx}-${idx}`}>{c.message}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </PolicyApplicabilityStepSection>
        )}
      </PolicyFormWizardShell>

      {configuringLeaveType && configuringCfg && (
        <ConfigureLeaveTypeModal
          darkMode={darkMode}
          leaveType={configuringLeaveType}
          cfg={configuringCfg}
          updateLeaveTypeConfig={updateLeaveTypeConfig}
          onClose={() => setConfiguringLeaveType(null)}
        />
      )}
    </>
  );
}
